import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminService } from '../../services/adminService';

const AdminPromotion = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  // NEW: categoriesInput for easier comma typing, just like AdminProduct
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    categories: [],
    categoriesInput: '',
    imageUrl: '',
    stockQuantity: '',
    isActive: true
  });

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const data = await adminService.getPromotions();
      setPromotions(data);
      setError(null);
    } catch (error) {
      setError(error.message);
      toast.error('Erreur lors du chargement des promotions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // This handler is now for the categories input as a string
  const handleCategoriesInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      categoriesInput: e.target.value
    }));
  };

  // On submit, parse categoriesInput to array
  const handleSubmit = async (e) => {
    e.preventDefault();
    const categoriesArr = formData.categoriesInput
      ? formData.categoriesInput.split(',').map(cat => cat.trim()).filter(Boolean)
      : [];
    try {
      const promotionData = {
        ...formData,
        price: parseFloat(formData.price),
        stockQuantity: parseInt(formData.stockQuantity),
        categories: categoriesArr,
        category: categoriesArr[0] || ''
      };

      if (editingPromotion) {
        await adminService.updatePromotion(editingPromotion.id, promotionData);
        toast.success('Promotion mise à jour avec succès');
      } else {
        await adminService.createPromotion(promotionData);
        toast.success('Promotion créée avec succès');
      }

      setShowForm(false);
      setEditingPromotion(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        categories: [],
        categoriesInput: '',
        imageUrl: '',
        stockQuantity: '',
        isActive: true
      });
      loadPromotions();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde de la promotion: ' + error.message);
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name || '',
      description: promotion.description || '',
      price: promotion.price || '',
      category: promotion.category || '',
      categories: Array.isArray(promotion.categories) ? promotion.categories : [],
      categoriesInput: Array.isArray(promotion.categories) ? promotion.categories.join(', ') : '',
      imageUrl: promotion.imageUrl || '',
      stockQuantity: promotion.stockQuantity || '',
      isActive: promotion.isActive !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) {
      try {
        await adminService.deletePromotion(id);
        toast.success('Promotion supprimée avec succès');
        loadPromotions();
      } catch (error) {
        toast.error('Erreur lors de la suppression de la promotion: ' + error.message);
      }
    }
  };

  const filteredPromotions = promotions.filter(promotion =>
    promotion.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    promotion.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    promotion.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD'
    }).format(price);
  };

  const totalValue = promotions.reduce(
    (sum, promotion) => sum + (promotion.price * promotion.stockQuantity), 0);

  return (
    <div className="admin-promotion" style={{ background: "#f4f5f7", minHeight: "100vh", paddingBottom: 40 }}>
      <div className="container" style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* HEADER */}
        <div className="admin-header" style={{ margin: "30px 0 22px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="admin-header-content">
            <div className="admin-breadcrumb" style={{ marginBottom: 6 }}>
              <Link to="/admin/dashboard" style={{ color: "#8374e6", textDecoration: "none" }}>
                <i className="fas fa-tachometer-alt"></i> Dashboard
              </Link>
              <span style={{ margin: "0 7px" }}>/</span>
              <span>Gestion des Promotions</span>
            </div>
            <h1 style={{ fontWeight: 700, fontSize: 28, color: "#222" }}>
              <i className="fas fa-tags" style={{ color: "#e74c3c", marginRight: 7 }}></i>
              Gestion des Promotions El Hiba Para
            </h1>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingPromotion(null);
              setFormData({
                name: '',
                description: '',
                price: '',
                category: '',
                categories: [],
                categoriesInput: '',
                imageUrl: '',
                stockQuantity: '',
                isActive: true
              });
            }}
            className="btn btn-primary"
            style={{
              background: "#e74c3c",
              border: "none",
              color: "#fff",
              padding: "10px 23px",
              fontWeight: 600,
              fontSize: 17,
              borderRadius: 10,
              boxShadow: "0 2px 10px #ffeaea"
            }}
          >
            <i className="fas fa-plus"></i> Ajouter une Promotion
          </button>
        </div>

        {/* STATS CARDS */}
        <div className="admin-stats" style={{
          display: "flex", gap: 24, marginBottom: 28
        }}>
          <div className="stat-card" style={{
            flex: 1, background: "#fff", borderRadius: 16, padding: "28px 18px 20px 22px", boxShadow: "0 2px 22px #f4eaea", display: "flex", alignItems: "center"
          }}>
            <div className="stat-icon" style={{ color: "#e74c3c", fontSize: 34, marginRight: 19 }}>
              <i className="fas fa-tags"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 26, color: "#222" }}>{promotions.length}</h3>
              <p style={{ margin: 0, color: "#9c9c9c", fontSize: 15 }}>Total Promotions</p>
            </div>
          </div>
          <div className="stat-card" style={{
            flex: 1, background: "#fff", borderRadius: 16, padding: "28px 18px 20px 22px", boxShadow: "0 2px 22px #f4eaea", display: "flex", alignItems: "center"
          }}>
            <div className="stat-icon" style={{ color: "#5dc25a", fontSize: 34, marginRight: 19 }}>
              <i className="fas fa-eye"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 26, color: "#222" }}>{promotions.filter(p => p.isActive !== false).length}</h3>
              <p style={{ margin: 0, color: "#9c9c9c", fontSize: 15 }}>Promotions Actives</p>
            </div>
          </div>
          <div className="stat-card" style={{
            flex: 1, background: "#fff", borderRadius: 16, padding: "28px 18px 20px 22px", boxShadow: "0 2px 22px #f4eaea", display: "flex", alignItems: "center"
          }}>
            <div className="stat-icon" style={{ color: "#f9ca24", fontSize: 34, marginRight: 19 }}>
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 26, color: "#222" }}>{formatPrice(totalValue)}</h3>
              <p style={{ margin: 0, color: "#9c9c9c", fontSize: 15 }}>Valeur Totale</p>
            </div>
          </div>
        </div>

        {/* SEARCH */}
        <div className="admin-search" style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14 }}>
          <div className="search-container" style={{ flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
            <i className="fas fa-search" style={{ color: "#e74c3c" }}></i>
            <input
              type="text"
              placeholder="Rechercher une promotion par nom, description ou catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1, fontSize: 16, border: "1.5px solid #e5e5e5",
                borderRadius: 8, padding: "7px 13px"
              }}
            />
          </div>
          <div className="search-info" style={{ fontSize: 15, color: "#8b8fa7" }}>
            Affichage de {filteredPromotions.length} promotion{filteredPromotions.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* PROMOTION FORM MODAL */}
        {showForm && (
          <div className="modal-overlay" style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.14)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <div className="modal-content" style={{
              background: "#fff", borderRadius: 18, boxShadow: "0 6px 32px #dcdcdc", padding: "28px 34px 22px 34px", minWidth: 340, maxWidth: 520, width: "100%", position: "relative"
            }}>
              <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 13 }}>
                <h2 style={{ fontWeight: 700, fontSize: 22, color: "#e74c3c" }}>
                  <i className="fas fa-tags"></i> {editingPromotion ? 'Modifier la Promotion' : 'Ajouter une Promotion'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="modal-close"
                  style={{ border: "none", background: "none", fontSize: 22, color: "#222", cursor: "pointer" }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="promotion-form">
                <div style={{ display: "flex", gap: 13 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="name" style={{ color: "#222", fontWeight: 600 }}>
                      <i className="fas fa-tag"></i> Nom de la promotion *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Ex: Crème hydratante en promotion"
                      style={{ width: "100%", margin: "4px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="price" style={{ color: "#222", fontWeight: 600 }}>
                      <i className="fas fa-money-bill-wave"></i> Prix (MAD) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder="Ex: 150.00"
                      style={{ width: "100%", margin: "4px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="description" style={{ color: "#222", fontWeight: 600 }}>
                    <i className="fas fa-align-left"></i> Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    placeholder="Description détaillée de la promotion..."
                    style={{ width: "100%", margin: "4px 0 14px 0", padding: "9px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 15 }}
                  ></textarea>
                </div>
                <div style={{ display: "flex", gap: 13 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="category" style={{ color: "#222", fontWeight: 600 }}>
                      <i className="fas fa-folder"></i> Catégorie principale
                    </label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      placeholder="Ex: Soins visage"
                      style={{ width: "100%", margin: "4px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="categories" style={{ color: "#222", fontWeight: 600, fontSize: 17 }}>
                      <i className="fas fa-tags"></i> Catégories (séparées par des virgules)
                    </label>
                    <input
                      type="text"
                      id="categories"
                      name="categories"
                      value={formData.categoriesInput}
                      onChange={handleCategoriesInputChange}
                      placeholder="Ex: Soins visage, Hydratation, Anti-âge"
                      style={{
                        width: "100%",
                        margin: "4px 0 14px 0",
                        padding: "10px 14px",
                        borderRadius: 7,
                        border: "1.2px solid #ececec",
                        fontSize: "18px",
                        minWidth: "340px",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 13 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="imageUrl" style={{ color: "#222", fontWeight: 600 }}>
                      <i className="fas fa-image"></i> URL de l'image
                    </label>
                    <input
                      type="url"
                      id="imageUrl"
                      name="imageUrl"
                      value={formData.imageUrl}
                      onChange={handleInputChange}
                      placeholder="https://exemple.com/image.jpg"
                      style={{ width: "100%", margin: "4px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="stockQuantity" style={{ color: "#222", fontWeight: 600 }}>
                      <i className="fas fa-boxes"></i> Quantité en stock *
                    </label>
                    <input
                      type="number"
                      id="stockQuantity"
                      name="stockQuantity"
                      value={formData.stockQuantity}
                      onChange={handleInputChange}
                      required
                      min="0"
                      placeholder="Ex: 50"
                      style={{ width: "100%", margin: "4px 0 14px 0", padding: "7px 11px", borderRadius: 7, border: "1.2px solid #ececec", fontSize: 16 }}
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 13 }}>
                  <label className="checkbox-label" style={{ color: "#222", fontWeight: 600, display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      style={{ marginRight: 7 }}
                    />
                    Promotion active
                  </label>
                </div>

                <div className="form-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <button type="button" onClick={() => setShowForm(false)} className="btn btn-outline"
                    style={{ padding: "7px 19px", borderRadius: 8, border: "1.3px solid #e0e0e0", background: "#fff", color: "#e74c3c", fontWeight: 600 }}>
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary"
                    style={{ background: "#e74c3c", border: "none", color: "#fff", padding: "8px 20px", fontWeight: 600, borderRadius: 8 }}>
                    <i className="fas fa-save"></i> {editingPromotion ? 'Mettre à jour' : 'Créer la promotion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* PROMOTIONS GRID */}
        {loading ? (
          <div className="loading-container" style={{ textAlign: "center", marginTop: 60 }}>
            <div className="loading-spinner" style={{ margin: "0 auto 15px auto" }}>
              <div className="spinner"></div>
            </div>
            <p style={{ color: "#e74c3c", fontSize: 18 }}>Chargement des promotions...</p>
          </div>
        ) : error ? (
          <div className="error-container" style={{ textAlign: "center", marginTop: 40 }}>
            <h3 style={{ color: "#e74c3c" }}>Erreur lors du chargement des promotions</h3>
            <p style={{ color: "#c0392b" }}>{error}</p>
            <button onClick={loadPromotions} className="btn btn-primary"
              style={{ background: "#e74c3c", color: "#fff", border: "none", padding: "7px 18px", borderRadius: 8, fontWeight: 600 }}>
              Réessayer
            </button>
          </div>
        ) : (
          <div className="promotions-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 32, marginTop: 12
          }}>
            {filteredPromotions.length === 0 ? (
              <div className="no-promotions" style={{ textAlign: "center", gridColumn: "1/-1", marginTop: 38 }}>
                <i className="fas fa-tags" style={{ fontSize: 40, color: "#e74c3c" }}></i>
                <h3>Aucune promotion trouvée</h3>
                <p>
                  {searchTerm
                    ? `Aucune promotion ne correspond à votre recherche "${searchTerm}"`
                    : 'Commencez par ajouter votre première promotion'
                  }
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn btn-primary"
                    style={{ background: "#e74c3c", color: "#fff", border: "none", padding: "9px 26px", borderRadius: 8, fontWeight: 600, fontSize: 17, marginTop: 9 }}
                  >
                    <i className="fas fa-plus"></i> Ajouter une promotion
                  </button>
                )}
              </div>
            ) : (
              filteredPromotions.map(promotion => (
                <div key={promotion.id} className="promotion-card" style={{
                  background: "#fff", borderRadius: 23, boxShadow: "0 2px 18px #f4eaea",
                  padding: "24px 18px 18px 18px", minHeight: 320, display: "flex", flexDirection: "column", alignItems: "center", position: "relative"
                }}>
                  <div className="promotion-image" style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 13 }}>
                    <img
                      src={promotion.imageUrl || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=500&h=300&fit=crop'}
                      alt={promotion.name}
                      style={{ maxWidth: 180, maxHeight: 130, borderRadius: 13, objectFit: "contain", background: "#fafafa", border: "1px solid #f2f2f2", boxShadow: "0 1px 7px #f7eaea" }}
                    />
                    <div style={{
                      position: "absolute", top: 18, left: 18, background: "#e74c3c", color: "#fff", fontWeight: 600,
                      fontSize: 13, borderRadius: 9, padding: "3px 11px", boxShadow: "0 1px 7px #ffeaea"
                    }}>
                      PROMO
                    </div>
                    <div style={{
                      position: "absolute", top: 18, right: 18,
                      background: promotion.isActive !== false ? "#5dc25a" : "#f39c12", color: "#fff",
                      borderRadius: 8, padding: "2.5px 10px", fontSize: 13, fontWeight: 600
                    }}>
                      {promotion.isActive !== false ? 'Actif' : 'Inactif'}
                    </div>
                  </div>

                  <div className="promotion-info" style={{ width: "100%" }}>
                    <h3 style={{ fontWeight: 700, fontSize: 20, color: "#e74c3c", marginBottom: 4 }}>{promotion.name}</h3>
                    <p className="promotion-category" style={{ color: "#8b8fa7", fontWeight: 500, fontSize: 15, marginBottom: 5 }}>
                      {Array.isArray(promotion.categories)
                        ? promotion.categories.join(', ')
                        : promotion.category || 'Non catégorisé'
                      }
                    </p>
                    <p className="promotion-price" style={{ fontWeight: 700, fontSize: 18, color: "#222", marginBottom: 5 }}>{formatPrice(promotion.price)}</p>
                    <p className="promotion-stock" style={{ color: promotion.stockQuantity > 0 ? "#5dc25a" : "#e74c3c", fontWeight: 600, marginBottom: 5 }}>
                      <i className={`fas ${promotion.stockQuantity > 0 ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                      {promotion.stockQuantity > 0 ? ` ${promotion.stockQuantity} en stock` : ' Rupture de stock'}
                    </p>
                    <p className="promotion-description" style={{ color: "#444", fontSize: 15, marginBottom: 10 }}>
                      {promotion.description?.substring(0, 100)}...
                    </p>
                  </div>

                  <div className="promotion-actions" style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: "auto" }}>
                    <button
                      onClick={() => handleEdit(promotion)}
                      className="btn btn-outline btn-small"
                      style={{ border: "1.3px solid #8374e6", color: "#8374e6", background: "#fff", borderRadius: 8, padding: "7px 15px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}
                    >
                      <i className="fas fa-edit"></i> Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(promotion.id)}
                      className="btn btn-danger btn-small"
                      style={{ border: "1.3px solid #e74c3c", color: "#fff", background: "#e74c3c", borderRadius: 8, padding: "7px 15px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}
                    >
                      <i className="fas fa-trash"></i> Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromotion;